<template>
    <div class="activity-log">
        <b-card no-body>
            <b-card-header class="p-3">
                <b-row align-v="center">
                    <b-col>
                        <h3 class="mb-0">
                            Activity Log
                        </h3>
                    </b-col>
                    <b-col cols="4">
                        <el-date-picker
                        v-model="date_picked"
                        type="date"
                        value-format="yyyy-MM-dd"
                        class="float-right py-0"
                        placeholder="Pick a day"
                        @change="findActivityLogs">
                        </el-date-picker>
                    </b-col>
                </b-row>
            </b-card-header>
            <b-row class="p-3" v-if="allActivityLogs.length > 0">
                <b-col 
                    cols="12" 
                    class="mb-3"
                    v-for="(log, index) in paginatedData"
                    :key="index"
                >
                    <div v-if="log.event_type === 'credit_report_purchase'">
                        <p class="mb--1 font-weight-normal text-sm">
                            <span class="font-weight-600">CTOS Report</span> was purchased by <span class="font-weight-600">{{ log.purchased_by }}</span>.
                        </p>
                        <small>{{ log.created_at | dateTimeFormatString }}</small>
                    </div>
                    <div v-if="log.event === 'created'">
                        <p class="mb--1 font-weight-normal text-sm">
                            <span class="text-capitalize font-weight-600">{{ log.attribute}}</span> 
                            was {{ log.event }} by <span class="font-weight-600">{{ log.causer || 'N/A' }}</span>.
                        </p>
                        <small>{{ log.date | dateTimeFormatString }}</small>
                    </div>
                    <div v-if="log.event === 'updated'">
                        <div v-if="log.new_value && !log.old_value">                       
                            <p class="mb--1 font-weight-normal text-sm">
                                <span class="text-capitalize font-weight-600">{{ log.attribute}}</span> 
                                was {{ log.event }} to <span  class="font-weight-600">{{ log.new_value }}</span> by <span class="font-weight-600">{{ log.causer || 'N/A' }}</span>.
                            </p>
                            <small>{{ log.date | dateTimeFormatString }}</small>
                        </div>
                        <div v-if="log.new_value && log.old_value">                       
                            <p class="mb--1 font-weight-normal text-sm">
                                <span class="text-capitalize font-weight-600">{{ log.attribute}}</span> 
                                was {{ log.event }} from <span  class="font-weight-600">{{ log.old_value }}</span>  to <span  class="font-weight-600">{{ log.new_value }}</span> by <span class="font-weight-600">{{ log.causer || 'N/A' }}</span>.
                            </p>
                            <small>{{ log.date | dateTimeFormatString }}</small>
                        </div>
                    </div>
                </b-col>
                <b-col cols="12">
                    <el-pagination
                        class="float-right"
                        background
                        :current-page="currentPage"
                        :page-size=10
                        layout="prev, pager, next"
                        hide-on-single-page
                        :total=totalItems
                        @current-change="handlePageChange"
                        >
                    </el-pagination>
                </b-col>
            </b-row>
            <div v-else class="p-3">
                <span class="font-weight-bold text-sm">No record found.</span>
            </div>
        </b-card>
    </div>
</template>
<script>
import { DatePicker, Pagination } from 'element-ui'
import {
notifyFunctionMixin,
helperFunctionMixin,
axios,
services,
getHeader
} from '@/assets/js/httpImports.js'
import { mapActions, mapState } from 'pinia'
import { useCustomerStore } from '@/stores/customerStore'

    export default {
        props: ['customer'],
        mixins: [notifyFunctionMixin, helperFunctionMixin],
        components: {
            [DatePicker.name]: DatePicker,
            [Pagination.name]: Pagination,
        },
        data() {
            return {
                date_picked: '',
                label: {
                    'platform_id': 'Platform',
                    'source_channel_id': 'Channel',
                    'main_status': 'Main Status',
                    'sub_status': 'Sub Status',
                    'contact_no': 'Contact No',
                    'meeting_id': 'Meeting',
                    'meeting_status': 'Meeting Status',
                    'start_at': 'Start Date',
                    'end_at': 'End Date',
                    'subject': 'Title'
                },
                totalItems: 0,
                paginatedData: [],
                currentPage: 1,
                pageSize: 10,
                activityLogLoaded: false,
                creditReportLogs: [],
                creditReportLogsLoaded: false,
                isLoadingCreditReports: false,
            }
        },

        computed:{
            ...mapState(useCustomerStore, ['current_customer_logs']),
            allActivityLogs() {
                let combined = [...this.current_customer_logs, ...this.creditReportLogs];
                
                if (this.date_picked) {
                    const selectedDate = this.date_picked;
                    combined = combined.filter(log => {
                        const logDate = log.date || log.created_at || log.credit_report_request_datetime;
                        if (!logDate) return false;
                        const logDateStr = logDate.split(' ')[0];
                        return logDateStr === selectedDate;
                    });
                }
                
                return combined.sort((a, b) => {
                    const dateA = new Date(a.date || a.created_at || a.credit_report_request_datetime);
                    const dateB = new Date(b.date || b.created_at || b.credit_report_request_datetime);
                    return dateB - dateA;
                });
            }
        },
        watch:{
            customer(newVal, oldVal){
                if(newVal && oldVal && newVal.customer_id !== oldVal.customer_id){
                    this.activityLogLoaded = false
                    this.creditReportLogsLoaded = false
                    this.getCustomerActivityLog(this.$route.params.customer_id)
                    this.getCreditReportLogs()
                }
            },
            allActivityLogs(newVal) {
                if(newVal) {
                    this.currentPage = 1
                    this.totalItems = newVal.length
                    this.updatePaginatedData()
                }
            }
        },
        async created(){
            if (!this.activityLogLoaded) {
                this.activityLogLoaded = true
                await this.getCustomerActivityLog(this.$route.params.customer_id)
            }
            if (!this.creditReportLogsLoaded) {
                this.creditReportLogsLoaded = true
                await this.getCreditReportLogs()
            }
            this.updatePaginatedData();
        },
        beforeDestroy() {
            this.activityLogLoaded = false;
        },
        methods:{
            ...mapActions(useCustomerStore,['getCustomerActivityLog']),
            async getCreditReportLogs() {
                if (this.isLoadingCreditReports) {
                    return;
                }

                const customerId = this.customer?.customer_id || this.$route.params.customer_id;
                if (!customerId) {
                    this.creditReportLogs = [];
                    return;
                }

                this.isLoadingCreditReports = true;
                try {
                    const queryParams = _gHlp.generateQueryParams({
                        paginate: 100,
                        page: 1,
                        orderBy: {
                            credit_report_id: 'desc'
                        },
                        search: {
                            credit_report_cust_id: customerId,
                            credit_report_status: 'completed',
                            credit_report_application_code: this.app_code
                        }
                    });

                    const response = await axios.get(
                        `${services.commitment}credit-reports?${queryParams}`,
                        { headers: getHeader() }
                    );

                    if (response.data && response.data.data) {
                        this.creditReportLogs = response.data.data.map(report => ({
                            event_type: 'credit_report_purchase',
                            created_at: report.credit_report_request_datetime,
                            credit_report_request_datetime: report.credit_report_request_datetime,
                            purchased_by: report.credit_report_created_by_details?.name || 'N/A',
                            credit_report_id: report.credit_report_id
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching credit report logs:', error);
                    this.creditReportLogs = [];
                } finally {
                    this.isLoadingCreditReports = false;
                }
            },
            findActivityLogs(){
                this.activityLogLoaded = false
                this.getCustomerActivityLog(this.$route.params.customer_id, this.date_picked)
            },
            updatePaginatedData() {
                const startIndex = (this.currentPage - 1) * this.pageSize;
                const endIndex = startIndex + this.pageSize;
                this.paginatedData = this.allActivityLogs.slice(startIndex, endIndex);
            },
            handlePageChange(page) {
                this.currentPage = page; // Update the current page when it changes
                this.updatePaginatedData();
                // this.fetchData(); // Fetch the data for the new page
            },
        }
    }
</script>